import {
  DailyProgramRangesByDay as GeneratedDailyProgramRangesByDay,
  DailyProgramRangeView as GeneratedDailyProgramRangeView,
  SubprogramsByApplianceCategory as GeneratedSubprogramsByApplianceCategory,
} from "@/.generated/hey/v4b-backend/types.gen";
import {
  BookableSpaceSubprogramRange,
  CommonSubprogramRange,
  EBookableSpaceMetaMode,
  ECommonProgramMetaMode,
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  HeaterSubprogramRange,
  OnSubprogramRange,
  SubprogramRange,
  SubprogramsByApplianceCategory,
  SubprogramsByApplianceCategoryRequest,
  WeeklySubprogram,
} from "@/domains/v4b/program/subprogram.types";
import { EDay } from "@/utils/date";
import { entries, keys } from "@/utils/object";
import { stringLocalTimeSchema } from "@/utils/time";

function mapSubprogramRange(dailyProgramRange: GeneratedDailyProgramRangeView): SubprogramRange {
  switch (dailyProgramRange.metaMode) {
    case "CONFORT":
    case "ECO":
    case "HORS_GEL":
      return {
        metaMode: dailyProgramRange.metaMode as EHeaterProgramMetaMode,
        startTime: stringLocalTimeSchema.parse(dailyProgramRange.startTime),
        endTime: stringLocalTimeSchema.parse(dailyProgramRange.endTime),
        temperature: undefined,
      } satisfies HeaterSubprogramRange;
    case "ON":
      return {
        metaMode: dailyProgramRange.metaMode as EOnProgramMetaMode,
        startTime: stringLocalTimeSchema.parse(dailyProgramRange.startTime),
        endTime: stringLocalTimeSchema.parse(dailyProgramRange.endTime),
        temperature: undefined,
      } satisfies OnSubprogramRange;
    case "OFF":
    case "TEMPERATURE":
      return {
        metaMode: dailyProgramRange.metaMode as ECommonProgramMetaMode,
        startTime: stringLocalTimeSchema.parse(dailyProgramRange.startTime),
        endTime: stringLocalTimeSchema.parse(dailyProgramRange.endTime),
        temperature: dailyProgramRange.temperature,
      } satisfies CommonSubprogramRange;
    case "AVAILABLE":
    case "OCCUPIED":
      return {
        metaMode: dailyProgramRange.metaMode as EBookableSpaceMetaMode,
        startTime: stringLocalTimeSchema.parse(dailyProgramRange.startTime),
        endTime: stringLocalTimeSchema.parse(dailyProgramRange.endTime),
        temperature: dailyProgramRange.temperature,
      } satisfies BookableSpaceSubprogramRange;
  }
}

function mapWeeklyProgram(dailyProgramRanges: GeneratedDailyProgramRangesByDay): WeeklySubprogram {
  return {
    [EDay.MONDAY]: dailyProgramRanges[EDay.MONDAY].map(mapSubprogramRange),
    [EDay.TUESDAY]: dailyProgramRanges[EDay.TUESDAY].map(mapSubprogramRange),
    [EDay.WEDNESDAY]: dailyProgramRanges[EDay.WEDNESDAY].map(mapSubprogramRange),
    [EDay.THURSDAY]: dailyProgramRanges[EDay.THURSDAY].map(mapSubprogramRange),
    [EDay.FRIDAY]: dailyProgramRanges[EDay.FRIDAY].map(mapSubprogramRange),
    [EDay.SATURDAY]: dailyProgramRanges[EDay.SATURDAY].map(mapSubprogramRange),
    [EDay.SUNDAY]: dailyProgramRanges[EDay.SUNDAY].map(mapSubprogramRange),
  };
}

export function mapSubprograms(subprograms: GeneratedSubprogramsByApplianceCategory): SubprogramsByApplianceCategory {
  const result: SubprogramsByApplianceCategory = {};

  for (const applianceCategory of keys(subprograms)) {
    const value = subprograms[applianceCategory];
    if (value) {
      result[applianceCategory] = mapWeeklyProgram(value);
    }
  }

  return result;
}

export function mapSubprogramsDto(subprograms: SubprogramsByApplianceCategory): SubprogramsByApplianceCategoryRequest {
  return entries(subprograms).reduce<SubprogramsByApplianceCategoryRequest>(
    (formattedSubprograms, [category, ranges]) => ({
      ...formattedSubprograms,
      [category]: { ranges },
    }),
    {},
  );
}
